import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, Clock, TrendingUp, TrendingDown, ShieldAlert, Eye, Award, Tag, CheckCircle2 } from 'lucide-react';
import { api, useAuth } from '../contexts/AuthContext';

const TechPerformancePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const targetUserId = userId || user?.id;

  useEffect(() => {
    if (!targetUserId) return;
    const load = async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/analytics/technician-performance/${targetUserId}?months=${months}`);
        setData(d);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [targetUserId, months]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data) return <div className="text-center py-20 text-slate-500">Dati non disponibili</div>;

  const isSelf = user?.id === targetUserId;
  const fasterThanGroup = data.delta_vs_group_pct !== null && data.delta_vs_group_pct < 0;

  return (
    <div className="space-y-6" data-testid="tech-performance-page">
      {!isSelf && (
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="perf-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
        </Button>
      )}

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Performance Tecnico</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-0.5" data-testid="perf-tech-name">{data.user_name}</h1>
          <p className="text-sm text-slate-500 mt-1">{data.user_email}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {data.under_observation && (
              <Badge className="bg-red-600 text-white text-xs flex items-center gap-1" data-testid="perf-under-obs">
                <Eye className="w-3 h-3" /> SOTTO OSSERVAZIONE
              </Badge>
            )}
            {data.rank_position && (
              <Badge className="bg-blue-600 text-white text-xs flex items-center gap-1">
                <Award className="w-3 h-3" /> Rank {data.rank_position}/{data.rank_total}
              </Badge>
            )}
          </div>
        </div>
        <select
          value={months}
          onChange={(e) => setMonths(parseInt(e.target.value))}
          className="border border-slate-200 rounded-md text-sm px-2 py-1"
          data-testid="perf-months-filter"
        >
          <option value="3">Ultimi 3 mesi</option>
          <option value="6">Ultimi 6 mesi</option>
          <option value="12">Ultimo anno</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="perf-stat-total"><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Ticket gestiti</div><div className="text-3xl font-extrabold text-slate-900">{data.total_tickets}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Chiusi / Aperti</div><div className="text-xl font-extrabold"><span className="text-emerald-600">{data.closed_count}</span> <span className="text-slate-400">/</span> <span className="text-amber-600">{data.open_count}</span></div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Tasso completamento</div><div className="text-3xl font-extrabold text-emerald-600">{data.completion_rate_pct}%</div></CardContent></Card>
        <Card data-testid="perf-stat-warnings"><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Cartellini (mese)</div><div className={`text-3xl font-extrabold ${data.under_observation ? 'text-red-600' : 'text-slate-900'}`}>{data.active_warnings_this_month}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-bold flex items-center gap-2"><Clock className="w-4 h-4 text-blue-600" /> Tempi di risoluzione</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500">Media tecnico</div>
              <div className="text-2xl font-extrabold text-slate-900" data-testid="perf-avg-self">{data.avg_resolution_human}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500">Mediana</div>
              <div className="text-2xl font-extrabold text-slate-900">{data.median_resolution_human}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500">Media squadra</div>
              <div className="text-2xl font-extrabold text-slate-500" data-testid="perf-avg-group">{data.group_avg_human}</div>
            </div>
          </div>
          {data.delta_vs_group_pct !== null && (
            <div
              className="mt-4 px-3 py-2 rounded-md flex items-center gap-2 text-sm font-semibold"
              style={{
                background: fasterThanGroup ? 'rgba(16, 185, 129, 0.12)' : 'rgba(220, 38, 38, 0.10)',
                color: fasterThanGroup ? '#047857' : '#991b1b',
              }}
              data-testid="perf-delta-banner"
            >
              {fasterThanGroup ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
              {fasterThanGroup
                ? `Più veloce della squadra di ${Math.abs(data.delta_vs_group_pct)}%`
                : `Più lento della squadra di ${data.delta_vs_group_pct}%`}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold flex items-center gap-2"><Tag className="w-4 h-4 text-blue-600" /> Top categorie</CardTitle></CardHeader>
          <CardContent>
            {data.top_categories.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna categoria</p>
            ) : (
              <div className="space-y-2" data-testid="perf-categories">
                {data.top_categories.map((c, i) => {
                  const max = data.top_categories[0]?.count || 1;
                  const pct = (c.count / max) * 100;
                  return (
                    <div key={c.category_id} className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="font-semibold text-slate-700 truncate">{c.category_name}</span>
                        <span className="font-mono text-slate-500 ml-2">{c.count}</span>
                      </div>
                      <div className="bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Trend mensile</CardTitle></CardHeader>
          <CardContent>
            {data.by_month.length === 0 ? (
              <p className="text-sm text-slate-500">Nessun dato</p>
            ) : (
              <div className="flex items-end gap-1 h-32" data-testid="perf-trend">
                {data.by_month.map((m, i) => {
                  const max = Math.max(...data.by_month.map(x => x.count), 1);
                  const h = (m.count / max) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                      <div className="text-[9px] font-mono text-slate-500">{m.count}</div>
                      <div className="w-full bg-emerald-500 rounded-t" style={{ height: `${h}%`, minHeight: '2px' }} />
                      <div className="text-[8px] font-mono text-slate-400 truncate w-full text-center">{m.month.slice(-5)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TechPerformancePage;
